import { Controller } from "@hotwired/stimulus";

const itemSelector = "[data-workflow-state-id]";

export default class extends Controller<HTMLElement> {
  static targets = ["item"];
  static values = { axis: String, reorderUrl: String };

  declare readonly axisValue: "horizontal" | "vertical";
  declare readonly itemTargets: HTMLElement[];
  declare readonly reorderUrlValue: string;

  private abortController?: AbortController;
  private draggedItem?: HTMLElement;

  connect() {
    this.abortController = new AbortController();

    this.itemTargets.forEach((item) => {
      item.addEventListener("dragstart", this.dragStart, this.listenerOptions);
      item.addEventListener("dragend", this.dragEnd, this.listenerOptions);
      item.addEventListener("dragover", this.dragOver, this.listenerOptions);
      item.addEventListener("drop", this.drop, this.listenerOptions);
    });
  }

  disconnect() {
    this.abortController?.abort();
  }

  private dragStart = (event: DragEvent) => {
    const item = event.currentTarget as HTMLElement;
    this.draggedItem = item;
    item.classList.add("dragging");

    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = "move";
      event.dataTransfer.setData("text/plain", item.dataset.workflowStateId ?? "");
    }
  };

  private dragEnd = () => {
    this.draggedItem?.classList.remove("dragging");
    this.itemTargets.forEach((item) => item.classList.remove("drop-before", "drop-after"));
    this.draggedItem = undefined;
  };

  private dragOver = (event: DragEvent) => {
    if (!this.draggedItem) return;

    event.preventDefault();
    if (event.dataTransfer) event.dataTransfer.dropEffect = "move";

    const target = event.currentTarget as HTMLElement;
    if (target === this.draggedItem) return;

    target.classList.toggle("drop-before", this.shouldDropBefore(event, target));
    target.classList.toggle("drop-after", !this.shouldDropBefore(event, target));
  };

  private drop = async (event: DragEvent) => {
    event.preventDefault();

    const target = event.currentTarget as HTMLElement;
    if (!this.draggedItem || target === this.draggedItem) return;

    const originalItems = this.itemTargets;
    const insertBefore = this.shouldDropBefore(event, target);

    target.classList.remove("drop-before", "drop-after");
    this.element.insertBefore(this.draggedItem, insertBefore ? target : target.nextElementSibling);

    try {
      await this.persistOrder();
    } catch (_error) {
      originalItems.forEach((item) => this.element.appendChild(item));
    }
  };

  private shouldDropBefore(event: DragEvent, target: HTMLElement) {
    const rect = target.getBoundingClientRect();
    const horizontal = this.axisValue === "horizontal";
    const midpoint = horizontal ? rect.left + rect.width / 2 : rect.top + rect.height / 2;

    return horizontal ? event.clientX < midpoint : event.clientY < midpoint;
  }

  private async persistOrder() {
    const response = await fetch(this.reorderUrlValue, {
      method: "PATCH",
      headers: {
        "Accept": "application/json",
        "Content-Type": "application/json",
        "X-CSRF-Token": this.csrfToken()
      },
      body: JSON.stringify({ workflow_state_ids: this.currentIds })
    });

    if (!response.ok) throw new Error("Workflow state reorder failed");
  }

  private csrfToken() {
    return document.querySelector<HTMLMetaElement>("meta[name='csrf-token']")?.content ?? "";
  }

  private get currentIds() {
    return Array.from(this.element.querySelectorAll<HTMLElement>(itemSelector)).map((item) => item.dataset.workflowStateId);
  }

  private get listenerOptions() {
    return { signal: this.abortController?.signal };
  }
}
